import { useEffect, useState, useCallback, useRef } from 'react'
import { HubConnectionBuilder, HubConnection, HubConnectionState } from '@microsoft/signalr'
import { ActiveUser } from '../components/code-index/ActiveUsersIndicator'
import { signalrConfig } from '../config/signalr.config'
import { getAccessToken } from '../auth/tokenManager'

interface UseCodesheetActiveUsersOptions {
  formatId: string | undefined
  enabled?: boolean
  identityKey?: string | null
  onMainMerged?: (event: CodeSheetMainMergedEvent) => void
}

export interface CodeSheetMainMergedEvent {
  codeIndexId: string
  sourceProjectId?: string | null
  revision?: string | null
  mergedAt?: string | null
}

interface UseCodesheetActiveUsersReturn {
  activeUsers: ActiveUser[]
  isConnected: boolean
  isConnecting: boolean
  connectionError: string | null
  reconnect: () => void
}

const MAX_RETRY_ATTEMPTS = 5

export const useCodesheetActiveUsers = ({
  formatId,
  enabled = true,
  identityKey,
  onMainMerged,
}: UseCodesheetActiveUsersOptions): UseCodesheetActiveUsersReturn => {
  const [activeUsers, setActiveUsers] = useState<ActiveUser[]>([])
  const [isConnected, setIsConnected] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [connectionError, setConnectionError] = useState<string | null>(null)
  const connectionRef = useRef<HubConnection | null>(null)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const tokenRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const isReconnectingRef = useRef<boolean>(false)
  const retryCountRef = useRef<number>(0)
  const onMainMergedRef = useRef(onMainMerged)

  useEffect(() => {
    onMainMergedRef.current = onMainMerged
  }, [onMainMerged])

  const startConnection = useCallback(async () => {
    if (!formatId || !enabled) return

    if (isReconnectingRef.current || 
        connectionRef.current?.state === HubConnectionState.Connected ||
        connectionRef.current?.state === HubConnectionState.Connecting) {
      return
    }

    if (retryCountRef.current >= MAX_RETRY_ATTEMPTS) {
      console.warn(`[SignalR] Max retry attempts (${MAX_RETRY_ATTEMPTS}) reached. Giving up.`)
      setIsConnecting(false)
      setConnectionError('Unable to connect to real-time service. Please refresh the page to try again.')
      return
    }

    try {
      isReconnectingRef.current = true
      setIsConnecting(true)
      setConnectionError(null)

      const connection = new HubConnectionBuilder()
        .withUrl(signalrConfig.hubUrl, {
          accessTokenFactory: async () => {
            try {
             
              const token = await getAccessToken()
              if (!token) {
               
                return ''
              }
             
              return token
            } catch (error) {
             
              return ''
            }
          },
          withCredentials: true
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: (retryContext: any) => {
            const { retryDelays } = signalrConfig
            if (retryContext.previousRetryCount === 0) return retryDelays.first
            if (retryContext.previousRetryCount === 1) return retryDelays.second
            if (retryContext.previousRetryCount === 2) return retryDelays.third
            return retryDelays.default
          }
        })
        .configureLogging({
          log: (logLevel: any, message: string) => {
            const isDebug = process.env.NODE_ENV === 'development' || 
                           localStorage.getItem('signalr_debug') === 'true'
            
            if (message.includes('There was an error with the transport') ||
              message.includes('Failed to start the transport')) {
              console.warn('[SignalR Transport]', message)
              return
            }
            
            // if (isDebug) {
            //   console.log(`[SignalR L${logLevel}]`, message)
            // } else if (logLevel >= 3) {
            //   console.error('[SignalR]', message)
            // } else if (logLevel >= 2) {
            //   console.warn('[SignalR]', message)
            // }
          }
        })
        .build()

      connection.on(signalrConfig.events.usersInFormatUpdated, (users: ActiveUser[]) => {
        setActiveUsers(users)
      })
      connection.on(signalrConfig.events.codeSheetMainMerged, (event: CodeSheetMainMergedEvent) => {
        onMainMergedRef.current?.(event)
      })

      connection.onreconnecting((error: any) => {
        setIsConnected(false)
        setIsConnecting(true)
        setConnectionError('Connection lost, reconnecting...')
      })

      connection.onreconnected((connectionId: any) => {
        retryCountRef.current = 0
        setIsConnected(true)
        setIsConnecting(false)
        setConnectionError(null)
        if (formatId) {
          connection.invoke(signalrConfig.methods.joinFormat, formatId).catch((err: any) => {
            console.error('Failed to rejoin format after reconnection:', err)
          })
        }
      })

      connection.onclose((error: any) => {
        isReconnectingRef.current = false
        setIsConnected(false)
        setIsConnecting(false)
        setActiveUsers([])
        
        const isAuthError = error?.message?.includes('401') || 
                           error?.message?.includes('Unauthorized') ||
                           error?.statusCode === 401
        
        retryCountRef.current++
        if (retryCountRef.current >= MAX_RETRY_ATTEMPTS) {
          console.warn(`[SignalR] Max retry attempts reached after connection close.`)
          setConnectionError('Unable to connect to real-time service. Please refresh the page to try again.')
          return
        }

        const delay = Math.min(5000 * Math.pow(2, retryCountRef.current - 1), 60000)
        if (isAuthError) {
          setConnectionError('Authentication failed - reconnecting...')
          reconnectTimeoutRef.current = setTimeout(() => startConnection(), 2000)
        } else {
          setConnectionError(error?.message || 'Connection closed')
          reconnectTimeoutRef.current = setTimeout(() => startConnection(), delay)
        }
      })

      await connection.start()

      await connection.invoke(signalrConfig.methods.joinFormat, formatId)
      

      connectionRef.current = connection
      retryCountRef.current = 0
      setIsConnected(true)
      setIsConnecting(false)
      isReconnectingRef.current = false
    } catch (error) {
      isReconnectingRef.current = false
      
      console.error('[SignalR] Connection error:', error)
      console.error('[SignalR] Error details:', {
        message: error instanceof Error ? error.message : String(error),
        name: error instanceof Error ? error.name : 'Unknown',
        stack: error instanceof Error ? error.stack : undefined
      })
      
      const isAuthError = error instanceof Error && 
                         (error.message?.includes('401') || 
                          error.message?.includes('Unauthorized'))
      
      retryCountRef.current++
      if (retryCountRef.current >= MAX_RETRY_ATTEMPTS) {
        console.warn(`[SignalR] Max retry attempts reached.`)
        setConnectionError('Unable to connect to real-time service. Please refresh the page to try again.')
        setIsConnecting(false)
        return
      }

      const delay = Math.min(5000 * Math.pow(2, retryCountRef.current - 1), 60000)
      if (isAuthError) {
        console.warn(`[SignalR] Auth error detected, retry ${retryCountRef.current}/${MAX_RETRY_ATTEMPTS} in 2s`)
        setConnectionError('Authentication failed - reconnecting...')
        reconnectTimeoutRef.current = setTimeout(() => startConnection(), 2000)
      } else {
        setConnectionError(error instanceof Error ? error.message : 'Failed to connect')
        setIsConnecting(false)
        console.warn(`[SignalR] Non-auth error, retry ${retryCountRef.current}/${MAX_RETRY_ATTEMPTS} in ${delay / 1000}s`)
        reconnectTimeoutRef.current = setTimeout(() => startConnection(), delay)
      }
    }
  }, [enabled, formatId, identityKey])

  const stopConnection = useCallback(async () => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
    
    if (tokenRefreshIntervalRef.current) {
      clearInterval(tokenRefreshIntervalRef.current)
      tokenRefreshIntervalRef.current = null
    }

    const connection = connectionRef.current
    if (connection) {
      connectionRef.current = null
      isReconnectingRef.current = false

      try {
        if (formatId && connection.state === HubConnectionState.Connected) {
          await connection.invoke(signalrConfig.methods.leaveFormat, formatId)
        }
        await connection.stop()
      } catch (error) {
        console.error('Error stopping SignalR connection:', error)
      } finally {
        setIsConnected(false)
        setIsConnecting(false)
        setActiveUsers([])
      }
    }
  }, [formatId, identityKey])

  const reconnect = useCallback(() => {
    retryCountRef.current = 0
    stopConnection().then(() => startConnection())
  }, [startConnection, stopConnection])

  useEffect(() => {
    if (enabled && formatId) {
      retryCountRef.current = 0
      startConnection()
    }
    return () => {
      stopConnection()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, formatId, identityKey])

  useEffect(() => {
    if (!enabled || !formatId) return

    const handleBeforeUnload = () => {
      const connection = connectionRef.current
      if (connection && connection.state === HubConnectionState.Connected) {
        connection.invoke(signalrConfig.methods.leaveFormat, formatId).catch(() => {})
      }
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        const connection = connectionRef.current
        if (connection && connection.state === HubConnectionState.Connected) {
          connection.invoke(signalrConfig.methods.leaveFormat, formatId).catch(() => {})
        }
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [enabled, formatId, identityKey])

  useEffect(() => {
    if (!enabled || !formatId) return

    const heartbeatInterval = setInterval(() => {
      const connection = connectionRef.current
      if (connection && connection.state === HubConnectionState.Connected) {
        connection.invoke('Heartbeat').catch(err => {
          if (err?.message?.includes('401') || err?.message?.includes('Unauthorized')) {
            reconnect()
          }
        })
      }
    }, 15000)

    return () => clearInterval(heartbeatInterval)
  }, [enabled, formatId, identityKey, reconnect])

  useEffect(() => {
    if (!enabled || !formatId) return
    
    const tokenRefreshInterval = setInterval(() => {
      const connection = connectionRef.current
      if (connection && connection.state === HubConnectionState.Connected) {
        reconnect()
      }
    }, 45 * 60 * 1000)

    tokenRefreshIntervalRef.current = tokenRefreshInterval

    return () => {
      if (tokenRefreshIntervalRef.current) {
        clearInterval(tokenRefreshIntervalRef.current)
        tokenRefreshIntervalRef.current = null
      }
    }
  }, [enabled, formatId, identityKey, reconnect])

  return {
    activeUsers,
    isConnected,
    isConnecting,
    connectionError,
    reconnect
  }
}

